/**
 * **`import 'trystero'` 在本项目里唯一允许出现的地方。**
 *
 * 这是 `old_repo` 最直接的一课：它的 `TinySimplePeer` 对象漏进了每一个
 * handler 签名，于是"换传输库"从一件事变成了几百处改动
 * （`docs/archive/netcode-s3-evaluation.md` §2.1）。这里的对策很土但有效 ——
 * **把那个 import 关进一个文件**，外面只看到 `Transport` 那个以 peer id
 * 为通货的小接口。
 *
 * ## 哪些事交给 Trystero，哪些不
 *
 * | 交给它 | 理由 |
 * | --- | --- |
 * | 信令、NAT 穿透、TURN | 那正是它的全部价值 |
 * | 请求/响应 + 超时 | `makeAction({kind:'request'})` 自带 `timeoutMs` |
 * | 可靠有序 | DataChannel 默认就是（`createDataChannel("data")`，无参数） |
 * | 背压 | `bufferedAmountLowThreshold = 65535` |
 *
 * | 我们自己做的 | 为什么它做不了 |
 * | --- | --- |
 * | **信封**（JSON 字符串） | 它不校验你的业务 schema |
 * | **PARDON_ME** | 它没有"结构化报障"的概念 |
 * | **心跳** | `ping()` 很快，但它**没有超时** —— 见下 |
 *
 * ⚠️ **`ping()` 没有超时。** 对端不回 pong 时它是一个永不落地的 Promise，
 * 所以这里必须自己 race 一个计时器。不这么做，心跳会死在第一次失败上
 * （`docs/netcode-s3.md` §3.1）。
 */

import {
  getRelaySockets as getNostrRelaySockets,
  joinRoom as joinNostrRoom,
  selfId as trysteroSelfId,
} from 'trystero';
import type { JoinRoomConfig, Room, TurnServerConfig } from 'trystero';

/** 转出去给 `host/online.ts` 用 —— `'trystero'` 的 import 只许出现在本文件 */
export type { TurnServerConfig };
import {
  getRelaySockets as getWsRelaySockets,
  joinRoom as joinWsRelayRoom,
  type WsRelayRoomConfig,
} from '@trystero-p2p/ws-relay';
import { TransportError, type Transport, type Unsubscribe } from './transport';
import { t } from '../i18n';

/** 两个 action 名字。**它们是线格式的一部分**，改了就等于不兼容 */
export const TRYSTERO_MESSAGE_ACTION = 's3/msg';
export const TRYSTERO_REQUEST_ACTION = 's3/rpc';

/**
 * **自选中继表。**
 *
 * ## 为什么必须自己指定
 *
 * Trystero 的默认列表有 28 个公共中继，但**只连前 5 个**，而且是按
 * `appId` 做确定性洗牌之后的前 5（`getRelays` 里 `shuffle(defaults,
 * strToNum(appId)).slice(0, 5)`）。我们的 appId 是 `ivory-roulette`，
 * 洗出来是：
 *
 * ```
 *   wss://basspistol.org                        ✓ 活着
 *   wss://relay.artio.inf.unibe.ch              ✓ 活着
 *   wss://relay-can.zombi.cloudrodion.com       ✗
 *   wss://nos.lol                               ✗（时好时坏）
 *   wss://nostr.tegila.com.br                   ✗ 已经没了
 * ```
 *
 * 评审看到的就是最后那一条：`WebSocket connection to
 * 'wss://nostr.tegila.com.br/' failed` —— 五个里三个是死的，浏览器每次重连
 * 都会往控制台打一条（**这不是我们代码能拦的**，是浏览器自己打的），
 * 而发现对端又被拖到几秒一次的重试节奏上，联机基本不可用。
 *
 * ## 怎么选的
 *
 * 用真浏览器逐个开 WebSocket + 发一条 `REQ`，看有没有回来的帧
 * （`scripts/smoke-online.mjs` 走的就是这套路）：
 *
 * | 中继 | 结果 |
 * | --- | --- |
 * | basspistol.org / relay.artio.inf.unibe.ch | ✓ 立刻回来 |
 * | relay.sigit.io / yabu.me/v2 / purplerelay.com | ✓ 回来 |
 * | damus / nos.lol / nostr.band / snort / primal / offchain / nostr.mom | ✗ 连不上（本机网络） |
 *
 * ⚠️ **两个玩家必须用同一张表**（房间靠这些中继上的 topic 互相发现）。
 * 表写在这里、两端同源，所以天然一致 —— 别做成"按 appId 洗牌取前几个"，
 * 那正是现在坏掉的原因。
 *
 * ⚠️ 想换成自建中继：加到这里，或者从 `connectRoom` 传 `relayUrls` 进来。
 */
export const DEFAULT_RELAYS: readonly string[] = [
  'wss://basspistol.org',
  'wss://relay.artio.inf.unibe.ch',
  'wss://relay.sigit.io',
  'wss://yabu.me/v2',
  'wss://purplerelay.com',
];
/**
 * 兜底 STUN。TURN 凭据端点只回 TURN（不带 STUN）时补上；Cloudflare 的公共
 * STUN 稳定、免凭据，和 TURN 同源。见 `resolveIceServers`。
 */
export const FALLBACK_STUN = 'stun:stun.cloudflare.com:3478';

/** 查询参数键：`?relay=wss://a,wss://b` 可临时覆盖中继表 */
const RELAY_QUERY_KEY = 'relay';
/** localStorage 键：本机固定一份中继表 */
const RELAY_STORE_KEY = 's3.relays';
/** 查询参数键：`?relayKind=ws|nostr` 指定信令策略 */
const RELAY_KIND_QUERY_KEY = 'relayKind';
/** localStorage 键：本机固定信令策略 */
const RELAY_KIND_STORE_KEY = 's3.relayKind';

/** 把一个逗号分隔的字符串解析成中继 URL 列表。不是 ws(s) 开头的一律丢掉 */
export function parseRelayList(raw: string | null | undefined): readonly string[] | null {
  if (raw === null || raw === undefined) return null;
  const urls = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.startsWith('wss://') || part.startsWith('ws://'));
  return urls.length > 0 ? urls : null;
}

/** 显式配置的中继表（查询参数 / localStorage / 构建期）。没有就是 null */
export function relayOverrideUrls(): readonly string[] | null {
  const fromEnv = parseRelayList(import.meta.env.VITE_RELAY_URLS);
  if (typeof window === 'undefined') return fromEnv;
  try {
    const fromQuery = parseRelayList(
      new URLSearchParams(window.location.search).get(RELAY_QUERY_KEY),
    );
    if (fromQuery !== null) return fromQuery;
    const fromStore = parseRelayList(window.localStorage.getItem(RELAY_STORE_KEY));
    if (fromStore !== null) return fromStore;
  } catch {
    // 隐私模式 / 某些环境禁了 localStorage —— 退回默认
  }
  return fromEnv;
}

/**
 * **默认 / 兜底的中继表（不是某个房间实际用的那一份）。**
 *
 * 优先级（先命中的赢）：
 *   1. 查询参数 `?relay=wss://a,wss://b`（临时、可分享）
 *   2. localStorage 的 `s3.relays`（本机固定）
 *   3. 构建期 `VITE_RELAY_URLS`（部署时固定）
 *   4. `DEFAULT_RELAYS`
 *
 * ⚠️ 房间实际用哪几台由 `relayUrlsForRoom()` 决定（没显式配置时按房间号切）。
 *    这个函数只回答“底表是什么”。
 */
export function resolveRelayUrls(): readonly string[] {
  return relayOverrideUrls() ?? DEFAULT_RELAYS;
}

/**
 * **信令策略。** Trystero 有好几套互不通话的策略：
 *
 * - `nostr`：默认表里那些公共 Nostr 中继；
 * - `ws`：`@trystero-p2p/ws-relay` 的自建 WebSocket 中继（如
 *   `wss://relay.onrwrp.org`）。
 *
 * ⚠️ **两端必须同为一种。** 拿 Nostr 客户端去连一个 ws-relay 服务器，
 *    或者反过来，都会"握上手却互相发现不了"（用户报的"加入者找不到房间"）。
 */
export type RelayKind = 'nostr' | 'ws';

/** 显式指定的信令策略（查询参数 / localStorage / 构建期）。没有就是 null */
export function relayKindOverride(): RelayKind | null {
  const parse = (raw: string | null | undefined): RelayKind | null => {
    const value = raw?.trim().toLowerCase();
    if (value === 'ws' || value === 'ws-relay' || value === 'wsrelay') return 'ws';
    if (value === 'nostr') return 'nostr';
    return null;
  };
  const fromEnv = parse(import.meta.env.VITE_RELAY_KIND);
  if (typeof window === 'undefined') return fromEnv;
  try {
    const fromQuery = parse(new URLSearchParams(window.location.search).get(RELAY_KIND_QUERY_KEY));
    if (fromQuery !== null) return fromQuery;
    const fromStore = parse(window.localStorage.getItem(RELAY_KIND_STORE_KEY));
    if (fromStore !== null) return fromStore;
  } catch {
    // 隐私模式 / 禁了 localStorage —— 退回构建期
  }
  return fromEnv;
}

/**
 * **这一局用哪种信令策略。**
 *
 * 默认 **Nostr**（内置那张 `DEFAULT_RELAYS` 是 Nostr，自建 Nostr 中继也走它；
 * `wss://relay.onrwrp.org` 实测就是一台 Nostr 中继）。
 *
 * 自建 `@trystero-p2p/ws-relay` 服务器才需要显式指定：
 * `?relayKind=ws` / localStorage `s3.relayKind=ws` / `VITE_RELAY_KIND=ws`。
 */
export function resolveRelayKind(): RelayKind {
  return relayKindOverride() ?? 'nostr';
}

/**
 * **一个房间用几个中继。**
 *
 * `1` = 用户提的"房间号模 N 选一台"：两端一定打同一台，代价是**没有冗余** ——
 * 那台不通，这个号就不通（换个号换一台）。调到 2–3 可以留兜底，但房间仍然
 * **由号决定是哪几台**，两端一致。
 */
export const ROOM_RELAY_COUNT = 1;

/** 房间号 → 稳定的 32 位散列（FNV-1a）。**纯函数，两端算出来一样** */
export function roomCodeSeed(code: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < code.length; i++) {
    hash ^= code.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * **从一张中继表里按房间号切出一段。**
 *
 * 纯函数，所以能在 node 里钉住两件事：同一个号**永远**得到同一批（两端一致）；
 * 不同号会分散到不同中继（不会所有房间挤一台）。
 */
export function roomRelaySlice(
  code: string,
  base: readonly string[] = DEFAULT_RELAYS,
  count: number = ROOM_RELAY_COUNT,
): readonly string[] {
  if (base.length === 0) return base;
  const take = Math.max(1, Math.min(count, base.length));
  if (base.length <= take) return base;
  const start = roomCodeSeed(code) % base.length;
  const out: string[] = [];
  for (let i = 0; i < take; i++) {
    const url = base[(start + i) % base.length];
    if (url !== undefined) out.push(url);
  }
  return out;
}

/**
 * **这个房间号要用哪几个中继。**
 *
 * 用户的设计：房间号隐含携带"用哪台中继" —— 于是**两端天然一致**，不再赌
 * "两人的网络恰好对同一批中继有交集"。
 *
 * ⚠️ 显式配置（`?relay=` / localStorage / `VITE_RELAY_URLS`）优先：自建中继时
 * 就照配置原样用，不再按房间号拆。
 */
export function relayUrlsForRoom(code: string): readonly string[] {
  const override = relayOverrideUrls();
  if (override !== null) return override;
  return roomRelaySlice(code, DEFAULT_RELAYS);
}

/** 一个中继 socket 的现场 */
export interface RelaySocketState {
  readonly url: string;
  /** WebSocket.readyState：0 CONNECTING / 1 OPEN / 2 CLOSING / 3 CLOSED */
  readonly readyState: number;
  readonly open: boolean;
}

/**
 * **现在 Trystero 真的握着哪些中继 socket。** 诊断用。
 *
 * 这是判断"两台机器是不是连到了同一批中继"的唯一直接证据：`getRelaySockets()`
 * 是 `@trystero-p2p/nostr` 的**全局**注册表（跨房间复用），返回 URL → WebSocket。
 * 控制台里 `DEV_TOOLS.net.relays()` 一敲，就能和对面比。
 */
export function relaySocketStates(): readonly RelaySocketState[] {
  const getter = resolveRelayKind() === 'ws' ? getWsRelaySockets : getNostrRelaySockets;
  const sockets = getter() as Record<string, WebSocket> | null | undefined;
  if (sockets === null || sockets === undefined) return [];
  const out: RelaySocketState[] = [];
  for (const [url, socket] of Object.entries(sockets)) {
    const readyState = socket?.readyState ?? -1;
    out.push({ url, readyState, open: readyState === 1 });
  }
  return out;
}

/**
 * **探一下信令到底通不通。**
 *
 * ## 为什么需要它
 *
 * 评审：「在修复网络问题之前，即便连不上网也能创建房间，这会误导玩家。」
 *
 * `joinRoom()` 是**同步返回**的 —— 它把 WebSocket 连接挂在后台，
 * 连不上也不抛，所以"房间建好了"和"网络通了"根本是两件事。
 * 这个函数把后者单独测一遍：对着中继表开 WebSocket，**任何一个真的
 * 握手成功**就算通。
 *
 * ## 为什么不是"给中继发一条 nostr 消息"
 *
 * 那能测得更彻底（能确定对方能收到我们发的），但它要求我们把 nostr 的
 * 信封格式也实现一遍 —— 而 trystero 就在同一棵树里，重复实现等于埋一个
 * "两边协议不一致"的坑。握手成功已经足够回答玩家真正关心的那个问题：
 * **这台机器现在能不能到这个中继**。
 *
 * ⚠️ 中继表必须和 `createTrysteroTransport` 用的是**同一张**
 * （两端也必须是同一张）。所以这个函数的默认参数就是 `DEFAULT_RELAYS`。
 */
export interface RelayProbeResult {
  readonly ok: boolean;
  /** 握手成功的那些（诊断用） */
  readonly open: readonly string[];
  /** 失败时给玩家看的一句话 */
  readonly reason: string | null;
}

/** 只用到 WebSocket 的这三个成员。测试可以塞一个假的进来 */
export interface ProbeSocket {
  onopen: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  close(): void;
}

export interface RelayProbeOptions {
  readonly urls?: readonly string[];
  readonly timeoutMs?: number;
  /** 造一个 socket。默认就是真的 `WebSocket` */
  readonly createSocket?: (url: string) => ProbeSocket;
}

/**
 * 开一堆 WebSocket，**第一个握手成功的就是答案**。
 *
 * 全部失败（或者超时）→ `ok: false`，理由大概分得清"一个都没连上"和
 * "等太久了"两种。两种对玩家的意思是一样的：现在联机不了。
 */
export async function probeRelays(options: RelayProbeOptions = {}): Promise<RelayProbeResult> {
  const urls = options.urls ?? resolveRelayUrls();
  const timeoutMs = options.timeoutMs ?? 4000;
  const createSocket =
    options.createSocket ?? ((url: string): ProbeSocket => new WebSocket(url) as unknown as ProbeSocket);

  if (urls.length === 0) return { ok: false, open: [], reason: t('host.relay.noneConfigured') };

  return await new Promise<RelayProbeResult>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const open: string[] = [];
    /** 每个探测 socket + 它握手成功没有。收尾时按这个决定怎么关（见 finish） */
    const sockets = new Map<string, { socket: ProbeSocket; open: boolean }>();

    /**
     * 收尾。
     *
     * ⚠️ **还处于 CONNECTING 的 socket 不能直接 close()** —— 浏览器会打
     * `WebSocket is closed before the connection is established`，而那条是
     * 浏览器自己打的、JS 拦不住（用户报的刷屏就是它）。所以：
     * - 已经握手成功的：立刻 close（干净，不报警告）；
     * - 还在连的：改成“一 open 就关”，或者连不上时让浏览器自己收尾。
     *
     * 代价是它可能比原来多连几百毫秒才退场，但换来的是控制台安静 ——
     * 而且那些 socket 本来就在往同一个中继连，不会多占一条真正的信道。
     */
    const finish = (result: RelayProbeResult): void => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      for (const entry of sockets.values()) {
        if (entry.open) {
          try {
            entry.socket.close();
          } catch {
            // 已经关了 / 造出来的假 socket 不支持 —— 探测定论，收尾不该再抛
          }
          continue;
        }
        entry.socket.onopen = () => {
          try {
            entry.socket.close();
          } catch {
            // 同上：收尾时不该再抛
          }
        };
        // 连不上的让浏览器自己报错收尾，别在这里 close() 触发那条警告
        entry.socket.onerror = null;
      }
      resolve(result);
    };

    timer = setTimeout(() => {
      finish({
        ok: false,
        open: [...open],
        reason: t('host.relay.timeout', { seconds: Math.round(timeoutMs / 1000) }),
      });
    }, timeoutMs);

    for (const url of urls) {
      let socket: ProbeSocket;
      try {
        socket = createSocket(url);
      } catch {
        continue;
      }
      const entry = { socket, open: false };
      sockets.set(url, entry);
      socket.onopen = () => {
        entry.open = true;
        open.push(url);
        finish({ ok: true, open: [...open], reason: null });
      };
      socket.onerror = () => {
        // ⚠️ 一个中继挂掉**不代表**网络不通（公共中继时好时坏是常态），
        //    所以只把它从"还在等"的集合里拿掉。**全部**都没了才算失败
        entry.open = false;
        socket.close();
        sockets.delete(url);
        if (sockets.size === 0 && !settled) {
          finish({ ok: false, open: [...open], reason: t('host.relay.allFailed') });
        }
      };
    }
  });
}

/**
 * 没配 TURN 时返回 `null` —— 交给 Trystero 用它自己那份默认 STUN。
 *
 * 配了 TURN 时**整份替换** ICE 服务器（`rtcConfig`），不再叠加 Trystero 的 4 条
 * 内置 STUN：端点返回的列表（通常已是 STUN + TURN 各一条）直接拿来用，列表里
 * 完全没有 STUN 时才补 [`FALLBACK_STUN`]，免得只剩 TURN 反而更慢。
 *
 * 抽成纯函数是为了能测「到底给出了几条、是哪几条」。
 */
export function resolveIceServers(
  turnServers: readonly TurnServerConfig[],
): RTCIceServer[] | null {
  if (turnServers.length === 0) return null;
  const hasStun = turnServers.some((server) =>
    (Array.isArray(server.urls) ? server.urls : [server.urls]).some((url) => String(url).startsWith('stun:')),
  );
  const list: TurnServerConfig[] = hasStun ? [...turnServers] : [...turnServers, { urls: FALLBACK_STUN }];
  return list as RTCIceServer[];
}

export interface TrysteroTransportOptions {
  /** 房间号。就是玩家念给朋友听的那 4 位 */
  readonly roomId: string;
  /** 应用 id。同一信令网络下用来分隔不同游戏的房间空间 */
  readonly appId: string;

  /** 房间密码（可选）。Trystero 会把它混进信令，不是加密 */
  readonly password?: string;
  /** TURN 服务器（Q12：V1 就要配，否则约 8% 的连接建不起来） */
  readonly turnServers?: readonly TurnServerConfig[];
  /** 自建信令中继。不给就用 Trystero 的默认公共中继 */
  readonly relayUrls?: readonly string[];
  /** 额外的 `RTCConfiguration` */
  readonly rtcConfig?: RTCConfiguration;
  /** 信令层报错（连不上中继等）。仅供观测 */
  readonly onError?: (error: unknown) => void;
}

export interface TrysteroTransport extends Transport {
  /** 底层 `Room`。**只给诊断用** —— 业务代码一律不许碰它 */
  readonly room: Room;
}

export function createTrysteroTransport(options: TrysteroTransportOptions): TrysteroTransport {
  const report = (error: unknown): void => {
    if (options.onError !== undefined) options.onError(error);
    else console.warn(`[s3] ${t('host.trystero.signalError')}`, error);
  };

  const iceServers = resolveIceServers(options.turnServers ?? []);
  const config: JoinRoomConfig = {
    appId: options.appId,
    ...(options.password === undefined ? {} : { password: options.password }),
    /**
     * ⚠️ **有 TURN 时用 `rtcConfig`，不用 `turnConfig`。**
     *
     * Trystero 的 `turnConfig` 会**叠加**它内置的 4 条默认 STUN；加上凭据端点
     * 返回的 STUN + TURN 就 ≥6 条 `iceServers` —— Chrome 会警告
     * 「Using five or more STUN/TURN servers slows down discovery」。
     * `rtcConfig` 是**整份替换**（不叠默认），端点的列表本来就带 STUN，
     * 于是只剩 2–3 条：不警告，候选发现也更快。
     */
    ...(iceServers !== null
      ? { rtcConfig: { ...(options.rtcConfig ?? {}), iceServers } }
      : options.rtcConfig === undefined
        ? {}
        : { rtcConfig: options.rtcConfig }),
    // ⚠️ 中继表**永远显式给**（默认那两个死中继就是这么来的，见 `DEFAULT_RELAYS`）；
    //    `warnOnRelayFailure: false` 把"某个中继拒了这条事件"的警告压掉 ——
    //    那是公共中继的常态（限流、CLOSED），不是我们的 bug，不该刷控制台
    relayConfig: {
      urls: [...(options.relayUrls ?? resolveRelayUrls())],
      warnOnRelayFailure: false,
    },
  };

  // ★ 按配置选策略：Nostr 表走默认，自建 WebSocket 中继走 ws-relay。
  //   以前一刀切用 Nostr，于是 `wss://relay.onrwrp.org` 这类 ws-relay
  //   服务器上两端"握上手却互相发现不了"。
  const joinHandlers = {
    onJoinError: (details: { readonly error: unknown }) =>
      report(new Error(t('host.trystero.joinFailed', { error: String(details.error) }))),
  };
  const room =
    resolveRelayKind() === 'ws'
      ? joinWsRelayRoom(config as unknown as WsRelayRoomConfig, options.roomId, joinHandlers)
      : joinNostrRoom(config, options.roomId, joinHandlers);

  const messageAction = room.makeAction<string>(TRYSTERO_MESSAGE_ACTION);
  const requestAction = room.makeAction<string, string>(TRYSTERO_REQUEST_ACTION, { kind: 'request' });

  const messageListeners = new Set<(from: string, raw: string) => void>();
  const requestHandlers = new Set<(from: string, raw: string) => string | Promise<string>>();
  const joinListeners = new Set<(id: string) => void>();
  const leaveListeners = new Set<(id: string) => void>();
  const errorListeners = new Set<(error: unknown) => void>();

  messageAction.onMessage = (data, ctx) => {
    for (const listener of messageListeners) listener(ctx.peerId, data);
  };

  requestAction.onRequest = (data, ctx) => {
    // 只会有 `S3Peer` 注册的那一个。多个的话取第一个 —— 一个请求只能有一个响应
    for (const handler of requestHandlers) return handler(ctx.peerId, data);
    throw new Error(`[s3] ${t('host.trystero.noRequestHandler')}`);
  };

  room.onPeerJoin = (peerId) => {
    for (const listener of joinListeners) listener(peerId);
  };
  room.onPeerLeave = (peerId) => {
    for (const listener of leaveListeners) listener(peerId);
  };

  let destroyed = false;

  return {
    selfId: trysteroSelfId,
    room,

    peers: () => Object.keys(room.getPeers()),

    send(to, raw) {
      if (destroyed) return;
      // 通知是"发了就不管"：失败交给心跳与 `resync` 发现，
      // 不在这里抛 —— 每个调用点都去 catch 一个它无能为力的异常没有意义
      const promise = to === null ? messageAction.send(raw) : messageAction.send(raw, { target: to });
      promise.catch((error: unknown) => {
        report(error);
        for (const listener of errorListeners) listener(error);
      });
    },

    async request(to, raw, timeoutMs) {
      if (destroyed) throw new TransportError('disconnected', t('host.trystero.disposed'));
      try {
        return await requestAction.request(raw, { target: to, timeoutMs });
      } catch (error) {
        throw translate(error);
      }
    },

    onMessage(handler): Unsubscribe {
      messageListeners.add(handler);
      return () => messageListeners.delete(handler);
    },

    onRequest(handler): Unsubscribe {
      requestHandlers.add(handler);
      return () => requestHandlers.delete(handler);
    },

    onPeerJoin(handler): Unsubscribe {
      joinListeners.add(handler);
      return () => joinListeners.delete(handler);
    },

    onPeerLeave(handler): Unsubscribe {
      leaveListeners.add(handler);
      return () => leaveListeners.delete(handler);
    },

    onError(handler): Unsubscribe {
      errorListeners.add(handler);
      return () => errorListeners.delete(handler);
    },

    async ping(id, timeoutMs) {
      if (destroyed) throw new TransportError('disconnected', t('host.trystero.disposed'));
      // ⚠️ Trystero 的 ping **没有超时**：对端不回 pong 就是永不落地。
      // 不 race 一个计时器的话，心跳会永远卡在第一次失败上
      let timer: ReturnType<typeof setTimeout> | null = null;
      try {
        return await Promise.race([
          room.ping(id),
          new Promise<never>((_resolve, reject) => {
            timer = setTimeout(() => reject(new TransportError('timeout', t('host.trystero.pingTimeout', { id }))), timeoutMs);
          }),
        ]);
      } catch (error) {
        throw translate(error);
      } finally {
        if (timer !== null) clearTimeout(timer);
      }
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      messageListeners.clear();
      requestHandlers.clear();
      joinListeners.clear();
      leaveListeners.clear();
      errorListeners.clear();
      room.onPeerJoin = null;
      room.onPeerLeave = null;
      void room.leave().catch(report);
    },
  };
}

function translate(error: unknown): TransportError {
  if (error instanceof TransportError) return error;
  const kind = (error as { kind?: string } | null)?.kind;
  const message = error instanceof Error ? error.message : String(error);
  switch (kind) {
    case 'timeout':
      return new TransportError('timeout', message);
    case 'aborted':
      return new TransportError('aborted', message);
    case 'rejected':
      return new TransportError('rejected', message);
    default:
      return new TransportError('disconnected', message);
  }
}
